/**
 * Submission Triage column metadata (OR-979).
 *
 * The table is column-driven: this file is the single source of truth for
 * WHICH columns exist per view, their group, width, sort field, and
 * whether they're visible in the simplified default view. Rendering of
 * each cell lives in `submissions-table.tsx` (keyed by column `id`), so
 * this stays a pure-data module (no JSX) — easy to feed the Customize
 * panel and the per-view toggle.
 *
 * Two views per the Figma "Deal vs Facility" toggle (frame 1597882362):
 *   - submissions (Deals) view
 *   - facilities view
 *
 * NOTE: most columns have no backend data on the list endpoint yet, so
 * their cells render "—" placeholders (same as today). Marked 🔧 in the
 * plan; surfaced for backend.
 */

export type TriageView = "submissions" | "facilities";

export type ColumnGroup = "uw" | "contact" | "facility" | "schedule" | "premium" | "quote";

export const GROUP_LABELS: Record<ColumnGroup, string> = {
    uw: "UW Information",
    contact: "Risk Management Contact",
    facility: "Facility View",
    schedule: "Schedule",
    premium: "Premium Selection",
    quote: "Quote",
};

/** Order the group bands / Customize sections render in. A single global
 *  order across both views; bands/sections with no columns simply don't
 *  render (see the `.filter(count > 0)` in the table). `facility` is a
 *  Customize-only group (via `customizeGroup`) — no column uses it as its
 *  table `group`, so it never renders a table band. */
export const GROUP_ORDER: ColumnGroup[] = ["uw", "contact", "facility", "schedule", "premium", "quote"];

export interface ColumnDef {
    /** Stable id — also the sort field sent to the API. */
    id: string;
    label: string;
    group: ColumnGroup;
    /** Overrides the group used in the Customize panel only (not the table
     *  band). Lets the facility-only columns sit under a "Facility View"
     *  section in Customize while staying in the UW band in the table. */
    customizeGroup?: ColumnGroup;
    width: number;
    /** Sortable header (chevron). Default false. */
    sortable?: boolean;
    /** Pinned to the left edge (Submission ID / Title). */
    sticky?: boolean;
    /** Shown in the simplified default view (before Customize). */
    defaultVisible?: boolean;
}

// ─────────────────────────── Submissions (Deals) view ───────────────────────────

export const SUBMISSIONS_COLUMNS: ColumnDef[] = [
    // UW Information
    { id: "submission_id", label: "Submission ID", group: "uw", width: 140, sortable: true, sticky: true, defaultVisible: true },
    { id: "title", label: "Submission Title", group: "uw", width: 240, sortable: true, sticky: true, defaultVisible: true },
    { id: "renewal_date", label: "Renewal Date", group: "uw", width: 130, sortable: true, defaultVisible: true },
    { id: "status", label: "Status", group: "uw", width: 170, sortable: true, defaultVisible: true },
    { id: "brokerage", label: "Brokerage", group: "uw", width: 180, sortable: true, defaultVisible: true },
    { id: "type", label: "Submission Type", group: "uw", width: 150, sortable: true, defaultVisible: true },
    { id: "submission_date", label: "Date of Submission", group: "uw", width: 160, sortable: true, defaultVisible: true },
    { id: "expiring_premium", label: "Total Expiring Premium", group: "uw", width: 180, sortable: true, defaultVisible: true },
    { id: "expiring_deductible", label: "Expiring Deductible", group: "uw", width: 150, sortable: true, defaultVisible: true },
    { id: "hubspot", label: "Hubspot Link", group: "uw", width: 120 },
    // Risk Management Contact (submission.contact)
    { id: "rm_name", label: "Name", group: "contact", width: 180, defaultVisible: true },
    { id: "rm_phone", label: "Phone", group: "contact", width: 160 },
    { id: "rm_email", label: "Email", group: "contact", width: 220 },
    // Schedule (bed counts + retro)
    { id: "facility_count", label: "#Communities", group: "schedule", width: 130, sortable: true, defaultVisible: true },
    { id: "skilled", label: "Skilled", group: "schedule", width: 110 },
    { id: "assisted", label: "Assisted", group: "schedule", width: 110 },
    { id: "independent", label: "Independent", group: "schedule", width: 120 },
    { id: "additional_exposure", label: "Add'l. Exposure", group: "schedule", width: 140 },
    { id: "retro_date", label: "Retro Date", group: "schedule", width: 130, sortable: true },
    // Premium Selection (submission.quote)
    { id: "quoted_deductible", label: "Quoted Deductible", group: "premium", width: 150, sortable: true },
    { id: "selected_premium", label: "Selected Premium", group: "premium", width: 160, sortable: true, defaultVisible: true },
    { id: "tria", label: "TRIA", group: "premium", width: 110 },
    { id: "gemini_fees", label: "Gemini Fees", group: "premium", width: 120 },
    { id: "retail_commission", label: "Retail Commission", group: "premium", width: 150 },
    { id: "tricura_fees", label: "Tricura Fees", group: "premium", width: 120 },
    { id: "deviation", label: "Deviation from Market", group: "premium", width: 170 },
    { id: "probability", label: "Probability to Bind", group: "premium", width: 160 },
    { id: "uw", label: "UW", group: "premium", width: 170, defaultVisible: true },
    { id: "approval_uw", label: "Approval UW", group: "premium", width: 170, defaultVisible: true },
    { id: "policy_number", label: "Policy Number", group: "premium", width: 160, sortable: true },
];

// ─────────────────────────── Facilities view ───────────────────────────

// Andressa's Loom (jun-17): the Facility View is simplified — the Schedule
// (facility address / beds / occupancy / retro) and Premium Selection /
// Quote sections were removed. It now shares most columns with the
// Submissions view; the only facility-specific extras are Broker Address /
// Broker State / Account Manager Name / Email (off by default).
export const FACILITIES_COLUMNS: ColumnDef[] = [
    { id: "submission_id", label: "Submission ID", group: "uw", width: 140, sortable: true, sticky: true, defaultVisible: true },
    { id: "facility_name", label: "Facility Name", group: "uw", width: 220, sortable: true, sticky: true, defaultVisible: true },
    { id: "ccn", label: "CCN", group: "uw", width: 140, defaultVisible: true },
    { id: "renewal_date", label: "Renewal Date", group: "uw", width: 130, sortable: true, defaultVisible: true },
    { id: "type", label: "Submission Type", group: "uw", width: 150, sortable: true, defaultVisible: true },
    { id: "submission_date", label: "Date of Submission", group: "uw", width: 160, sortable: true },
    { id: "brokerage", label: "Brokerage", group: "uw", width: 180, sortable: true, defaultVisible: true },
    { id: "broker_name", label: "Broker Name", group: "uw", width: 160, defaultVisible: true },
    // Facility location (OR-1386). The search is meant to find a facility by
    // its address / zip when the name is unknown (account clearance), so the
    // matched value has to be visible somewhere in the row. Off by default —
    // the Loom deliberately dropped the Schedule columns from this view, so
    // turning any of these on by default is design's call, not ours. Zip
    // stays "—" until backend OR-1387 adds `schedule.zip_code` to the wire.
    { id: "facility_address", label: "Address", group: "schedule", width: 220 },
    { id: "facility_city", label: "City", group: "schedule", width: 140 },
    { id: "facility_state", label: "State", group: "schedule", width: 90 },
    { id: "facility_zip", label: "Zip", group: "schedule", width: 100 },
    // Facility-specific extras — UW band in the table, but grouped under a
    // "Facility View" section in Customize (Andressa's Loom). Off by
    // default (🔧 not in view=facility yet). Account Manager Email before Name.
    { id: "broker_email", label: "Broker Email", group: "uw", customizeGroup: "facility", width: 220 },
    { id: "broker_address", label: "Broker Address", group: "uw", customizeGroup: "facility", width: 200 },
    { id: "broker_state", label: "Broker State", group: "uw", customizeGroup: "facility", width: 120 },
    { id: "account_manager_email", label: "Account Manager Email", group: "uw", customizeGroup: "facility", width: 220 },
    { id: "account_manager_name", label: "Account Manager Name", group: "uw", customizeGroup: "facility", width: 200 },
];

export function columnsForView(view: TriageView): ColumnDef[] {
    return view === "facilities" ? FACILITIES_COLUMNS : SUBMISSIONS_COLUMNS;
}

/** Default visible column ids for a view (the simplified table). */
export function defaultVisibleIds(view: TriageView): string[] {
    return columnsForView(view)
        .filter((c) => c.defaultVisible)
        .map((c) => c.id);
}

/** Every column id for a view, in their natural (default) order. Seeds
 *  the Customize drag-to-reorder state. */
export function allColumnIds(view: TriageView): string[] {
    return columnsForView(view).map((c) => c.id);
}
